// Email previews: the same renderer the sender uses, shown in a sandboxed frame.

import { useMemo } from 'preact/hooks';
import { render, monthLabel, type FooterSettings } from '../../shared/render';
import type { Block } from '../../shared/blocks';
import type { LookId, Settings } from '../../shared/types';

export function footerOf(s: Settings | undefined | null): FooterSettings {
  return {
    address: s?.address ?? '',
    phone: s?.phone ?? '',
    website: s?.website ?? '',
    from_name: s?.from_name ?? 'LaVir Med Spa',
    instagram: s?.instagram ?? '',
  };
}

/** The name previews use where an email says {{first_name}}, so Jen sees what a client sees. */
export const SAMPLE_NAME = 'Sarah';
export const fillName = (t: string) => t.replace(/\{\{\s*first[_ ]name\s*\}\}/gi, SAMPLE_NAME);

export function previewHtml(e: { blocks: Block[]; look: LookId; subject: string; preheader: string }, s: Settings | null | undefined, editable = false, when = new Date()): string {
  const html = render(
    { blocks: e.blocks, look: e.look, subject: e.subject, preheader: e.preheader },
    { baseUrl: location.origin, settings: footerOf(s), mode: 'preview', editable, dateLabel: monthLabel(when, s?.timezone) },
  ).html;
  // The editor keeps the tag so she can see and change it. Everywhere else shows the name.
  return editable ? html : fillName(html);
}

/** A scaled, non-interactive picture of an email. */
export function MiniEmail({ html, width, height, frameWidth = 600 }: { html: string; width: number; height: number; frameWidth?: number }) {
  const scale = width / frameWidth;
  const doc = useMemo(() => html.replace('</head>', '<style>html,body{overflow:hidden!important}</style></head>'), [html]);
  return (
    <div class="mini" style={{ width: `${width}px`, height: `${height}px` }}>
      <iframe
        srcdoc={doc}
        title="Email preview"
        tabIndex={-1}
        aria-hidden="true"
        loading="lazy"
        sandbox="allow-same-origin"
        style={{ width: `${frameWidth}px`, height: `${height / scale}px`, transform: `scale(${scale})` }}
      />
    </div>
  );
}
